import { h } from 'preact';
import { clamp } from 'utils/math';
import { joinClasses } from 'utils/utils';
import { replaceWithVNode } from 'utils/vnode';

import Dropdown from 'components/dropdown/dropdown';

import s from 'components/dropdown/autocomplete.sss';

export default class Autocomplete extends Dropdown {
	s = s;

	constructor(props) {
		super(props);

		// Initialize search with the selected option's label
		this.state.search = this.getLabelForValue(this.state.value, props) || '';
		// Value of the keyboard-highlighted option, not yet selected
		this.state.highlighted = null;

		this.onSearchInput = this.onSearchInput.bind(this);
		this.onSearchFocus = this.onSearchFocus.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
	}

	componentDidUpdate(prevProps, prevState) {
		super.componentDidUpdate(prevProps, prevState);

		if (prevState.highlighted !== this.state.highlighted) {
			try {
				this.base.querySelector(`.${s.highlighted}`)?.scrollIntoView({ block: 'nearest' });
			} catch {}
		}
	}

	get custom() {
		const { value } = this.state;
		if (value == null) return false;
		return !this.getOptions().includes(value);
	}

	get hasCustomOption() {
		return this.findOptions().some(opt => opt.attributes?.['data-custom']);
	}

	getOptions(props = this.props) {
		// Exclude custom option from the options list
		return this.findOptions(props)
			.filter(opt => !opt.attributes?.['data-custom'])
			.map(opt => opt.attributes.value);
	}

	sanitizeValue(value, options = this.getOptions()) {
		// If custom option exists, accept any value
		if (this.hasCustomOption) return value;
		return super.sanitizeValue(value, options);
	}

	getLabelForValue(value, props = this.props) {
		// No label for null/undefined values
		if (value == null) return '';
		const label = super.getLabelForValue(value, props);
		return label || value;
	}

	setInternalValue(value, props = this.props) {
		this.setState({
			value,
			...(value ? { search: this.getLabelForValue(value, props) || '' } : {}),
		});
	}

	setValue(value) {
		// Always keep search - in Autocomplete, search reflects the value's label
		super.setValue(value, true);
	}

	open() {
		const popover = this.base.querySelector('[popover]');
		if (popover && !popover.matches(':popover-open')) {
			try {
				popover.showPopover();
			} catch {
				// Ignore if popover is mid-transition
			}
		}
	}

	close() {
		// Prevent onToggle from re-opening the dropdown
		this._justSelected = true;
		super.close();
	}

	onClear(e) {
		super.onClear(e);
		this.setState({ search: '', highlighted: null }); // Clear search when clearing value
	}

	getVisibleOptions() {
		const inputs = this.base?.querySelectorAll('[popover] input[type="radio"]:not(:disabled)') || [];
		return [...inputs].map(input => input.value);
	}

	onKeyDown(e) {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();
			e.stopPropagation();
			this.open();

			const options = this.getVisibleOptions();
			if (!options.length) return;

			const down = e.key === 'ArrowDown';
			const current = options.indexOf(this.state.highlighted);
			const next = current === -1
				? (down ? 0 : options.length - 1)
				: clamp(current + (down ? 1 : -1), 0, options.length - 1);

			this.setState({ highlighted: options[next] });
		} else if (e.key === 'Enter') {
			e.preventDefault();
			e.stopPropagation();
			const { highlighted } = this.state;
			if (highlighted != null) this.setValue(highlighted);
			this.close();
		} else if (e.key === 'Escape') {
			this.close();
		}
	}

	onToggle(e) {
		// If input is still focused and popover was closed, re-open it
		// This handles spacebar accidentally triggering the button's popovertarget
		// But don't re-open if an item was just selected
		if (e.newState === 'closed' && !this._justSelected) {
			const input = this.base?.querySelector('input');
			if (input && document.activeElement === input) {
				// Defer to next tick to avoid fighting with the toggle
				setTimeout(() => this.open(), 0);
				return;
			}
		}
		this._justSelected = false;
		if (e.newState === 'closed') {
			this.setState({ highlighted: null });
		}
		// Focus input when popover opens
		if (e.newState === 'open') {
			const input = this.base?.querySelector('input');
			input?.focus();
		}
		super.onToggle(e);
	}

	onSearchInput(e) {
		const search = e.target.value;
		// Deselect value when typing
		this.setState({
			search,
			value: null,
			highlighted: null,
		});
		this.triggerChange();
		this.props.onInput?.(e);
		this.open();
	}

	onSearchFocus() {
		this.open();
	}

	hasExactMatch(search, props = this.props) {
		if (Array.isArray(search)) search = search.join('');
		const searchLower = search?.toLowerCase?.() || null;

		if (!searchLower) return false;

		return this.findOptions(props).some(opt => {
			if (opt.attributes?.['data-custom']) return false;
			const label = (opt.children || '').toString().toLowerCase();
			return label === searchLower;
		});
	}

	renderOption(element) {
		// Handle custom option: show when search exists, disable if exact match
		if (element.attributes?.['data-custom']) {
			const { search } = this.state;
			if (!search) return null;

			// Flatten children to string - VNode children may be an array
			let label = element.children || 'Create "%s"';
			if (Array.isArray(label)) label = label.join('');

			const content = replaceWithVNode(label, '%s', () => search);
			element = {
				...element,
				children: content,
				attributes: {
					...element.attributes,
					value: search,
					disabled: element.attributes.disabled || element.attributes.shouldDisable?.(search) || this.hasExactMatch(search),
				},
			};
		}

		// Add highlight to the option if it is the currently highlighted value
		if (element.attributes.value === this.state.highlighted) {
			element = {
				...element,
				attributes: {
					...element.attributes,
					class: joinClasses(element.attributes.class, s.highlighted),
				},
			};
		}

		return super.renderOption(element);
	}

	renderLabel(props = this.props, state = this.state) {
		return [
			!!props.preText && <span class={s.preText}>{props.preText}</span>,
			<input
				id="search-autocomplete"
				type="text"
				class={s.input}
				value={state.search}
				placeholder={props.placeholder}
				onInput={this.onSearchInput}
				onFocus={this.onSearchFocus}
				onKeyDown={this.onKeyDown}
				disabled={props.disabled}
				autofocus={props.autofocus}
			/>,
		];
	}

	renderPopoverContent(props = this.props, state = this.state) {
		return (
			<div
				class={this.s.list}
				data-search={state.search}
				tabIndex="-1"
				onClickCapture={this.close}
			>
				{this.renderOptions(props)}
			</div>
		);
	}

	render(props, state) {
		const vnode = super.render(props, state);
		// Prevent the button from being focused by default, so that the input can be focused instead
		vnode.attributes.tabIndex = -1;
		return vnode;
	}
}
